/**
 * Based on Justified Gallery - v3.8.1
 * http://miromannino.github.io/Justified-Gallery/
 *
 * Copyright (c) 2019 Miro Mannino
 * Licensed under the MIT license.
 */

export interface Options {
  // required? required to be > 0?
  rowHeight?: number;

  // false or negative value to deactivate. Positive number to express the value in pixels,
  // A string '[0-9]+%' to express in percentage (e.g. 300% means that the row height
  // can't exceed 3 * rowHeight)
  maxRowHeight?: string | number | false;

  // maximum number of rows to be displayed (0 = disabled)
  maxRowsCount?: number;

  // … which is the same as 'left', or can be 'justify', 'center', 'right' or 'hide'
  lastRow?: 'justify' | 'nojustify' | 'left' | 'center' | 'right' | 'hide';

  margins?: number;

  // negative value = same as margins, 0 = disabled, any other value to set the border
  border?: number;

  waitThumbnailsLoad?: boolean;

  // randomize order of photos
  randomize?: boolean;

  // The selector that is used to know what are the entries of the gallery
  selector?: string;

  // The selector that is used to know what are the images of each entry
  imgSelector?: string;

  // regexp to capture the extension of an image
  extension?: RegExp;

  // time interval (in ms) to check if the page changes its width
  refreshTime?: number;

  // change in width allowed (in px) without re-building the gallery
  refreshSensitivity?: number;

  // rewrite the rel of each analyzed links
  rel?: string;

  // rewrite the target of all links
  target?: string;

  // if row width / available space > 0.90 it will be always justified
  // (i.e. lastRow setting is not considered)
  // justifyThreshold must be in the interval [0,1]
  justifyThreshold?: number;

  // This is called to trigger events, the default behavior is to call $.trigger
  // Consider that 'this' is this set to the JustifiedGallery object, so it can
  // access to fields such as $gallery, useful to trigger events with jQuery.
  // Events
  //  - jg.complete : called when all the gallery has been created
  //  - jg.resize : called when the gallery has been resized
  //  - jg.rowflush : when a new row appears
  triggerEvent?: (event: 'jg.complete' | 'jg.resize' | 'jg.rowflush' | 'jg.destroy') => void;
}

export default class JustifiedGallery {
  private settings: Required<Options>;
  private imgAnalyzerTimeout?: NodeJS.Timeout;
  private entries: HTMLElement[] = [];
  private buildingRow = {
    entriesBuff: [] as HTMLElement[],
    width: 0,
    height: 0,
    aspectRatio: 0,
  };
  private lastFetchedEntry = 0;
  private lastAnalyzedIndex = -1;
  private yield = {
    every: 2, // do a flush every n flushes (must be greater than 1)
    flushed: 0, // flushed rows without a yield
  };
  private border: number;
  private maxRowHeight: number | false | undefined;
  private offY: number;
  private rows = 0;
  private checkWidthIntervalId?: NodeJS.Timeout;
  private galleryWidth: number;
  private gallery: HTMLElement;
  private galleryHeightToSet?: number;

  private entryWidths = new WeakMap<HTMLElement, number>();
  private entryHeights = new WeakMap<HTMLElement, number>();
  private entryJWidths = new WeakMap<HTMLElement, number>();
  private entryJHeights = new WeakMap<HTMLElement, number>();
  private entryLoaded = new WeakMap<HTMLElement, 'true' | 'false' | 'skipped' | 'error'>();

  private defaults: Options = {
    rowHeight: 120,
    maxRowHeight: false,
    maxRowsCount: 0,
    margins: 1,
    border: -1,
    lastRow: 'nojustify',
    justifyThreshold: 0.9,
    waitThumbnailsLoad: false,
    rel: undefined,
    target: undefined,
    extension: /\.[^./\\]+$/,
    refreshTime: 200,
    refreshSensitivity: 0,
    randomize: false,
    selector: 'div.photo',
    imgSelector: ':scope > img, :scope > a > img, :scope > svg, :scope > a > svg',
    triggerEvent: function (this: JustifiedGallery, event) {
      this.gallery.dispatchEvent(new Event(event));
    },
  };

  /**
   * Justified Gallery controller constructor
   *
   * @param gallery the gallery to build
   * @param settings the settings (the defaults are in JustifiedGallery.defaults)
   * @constructor
   */
  constructor(gallery: HTMLElement, settings: Options) {
    this.settings = { ...this.defaults, ...settings } as Required<Options>;

    this.gallery = gallery;
    this.gallery.classList.add('justified-gallery');

    this.border = this.settings.border >= 0 ? this.settings.border : this.settings.margins;
    this.offY = this.border;
    this.maxRowHeight = this.retrieveMaxRowHeight();
    this.galleryWidth = this.gallery.getBoundingClientRect().width;
  }

  private get galleryHeight(): number {
    return this.gallery.getBoundingClientRect().height;
  }

  private set galleryHeight(height: number) {
    this.gallery.style.height = `${height}px`;
  }

  /** @returns the image in the given entry */
  private imgFromEntry(entry: HTMLElement): HTMLImageElement | null {
    return entry.querySelector<HTMLImageElement>(this.settings.imgSelector);
  }

  /**
   * Display the entry
   *
   * @param $entry the entry to display
   * @param x the x position where the entry must be positioned
   * @param y the y position where the entry must be positioned
   * @param rowHeight the row height of the row that owns the entry
   */
  private displayEntry(entry: HTMLElement, x: number, y: number, rowHeight: number) {
    const imgWidth = this.entryJWidths.get(entry) ?? 0;
    const imgHeight = this.entryJHeights.get(entry) ?? 0;

    entry.style.width = `${imgWidth}px`;
    entry.style.height = `${rowHeight}px`;
    entry.style.top = `${y}px`;
    entry.style.left = `${x}px`;

    const image = this.imgFromEntry(entry);
    if (image) {
      image.style.width = `${imgWidth}px`;
      image.style.height = `${imgHeight}px`;
      image.style.marginLeft = `${-imgWidth / 2}px`;
      image.style.marginTop = `${-imgHeight / 2}px`;
    }

    entry.classList.add('jg-entry-visible');
  }

  /**
   * Clear the building row data to be used for a new row
   */
  private clearBuildingRow() {
    this.buildingRow.entriesBuff = [];
    this.buildingRow.aspectRatio = 0;
    this.buildingRow.width = 0;
  }

  /**
   * Justify the building row, preparing it to
   *
   * @param isLastRow
   * @param hiddenRow undefined or false for normal behavior. hiddenRow = true to hide the row.
   * @returns a boolean to know if the row has been justified or not
   */
  private prepareBuildingRow(isLastRow: boolean, hiddenRow: boolean) {
    let newImgW;
    let newImgH;
    let justify = true;
    let minHeight = 0;
    let availableWidth =
      this.galleryWidth - 2 * this.border - (this.buildingRow.entriesBuff.length - 1) * this.settings.margins;
    const rowHeight = availableWidth / this.buildingRow.aspectRatio;
    let defaultRowHeight = this.settings.rowHeight;
    const justifiable = this.buildingRow.width / availableWidth > this.settings.justifyThreshold;

    // Skip the last row if we can't justify it and the lastRow == 'hide'
    if (hiddenRow || (isLastRow && this.settings.lastRow === 'hide' && !justifiable)) {
      for (const entry of this.buildingRow.entriesBuff) {
        entry.classList.remove('jg-entry-visible');
      }
      return -1;
    }

    // With lastRow = nojustify, justify if is justifiable (the images will not become too big)
    if (isLastRow && !justifiable && this.settings.lastRow !== 'justify' && this.settings.lastRow !== 'hide') {
      justify = false;

      if (this.rows > 0) {
        defaultRowHeight = (this.offY - this.border - this.settings.margins * this.rows) / this.rows;
        justify = (defaultRowHeight * this.buildingRow.aspectRatio) / availableWidth > this.settings.justifyThreshold;
      }
    }

    for (let index = 0; index < this.buildingRow.entriesBuff.length; index++) {
      const entry = this.buildingRow.entriesBuff[index];
      const imgAspectRatio = (this.entryWidths.get(entry) ?? 1) / (this.entryHeights.get(entry) ?? 1);

      if (justify) {
        newImgW = index === this.buildingRow.entriesBuff.length - 1 ? availableWidth : rowHeight * imgAspectRatio;
        newImgH = rowHeight;
      } else {
        newImgW = defaultRowHeight * imgAspectRatio;
        newImgH = defaultRowHeight;
      }

      availableWidth -= Math.round(newImgW);
      this.entryJWidths.set(entry, Math.round(newImgW));
      this.entryJHeights.set(entry, Math.ceil(newImgH));
      if (index === 0 || minHeight > newImgH) minHeight = newImgH;
    }

    this.buildingRow.height = minHeight;
    return justify;
  }

  /**
   * Flush a row: justify it, modify the gallery height accordingly to the row height
   *
   * @param isLastRow
   * @param hiddenRow undefined or false for normal behavior. hiddenRow = true to hide the row.
   */
  private flushRow(isLastRow: boolean, hiddenRow: boolean) {
    const settings = this.settings;
    let offX = this.border;

    const buildingRowResult = this.prepareBuildingRow(isLastRow, hiddenRow);
    if (hiddenRow || (isLastRow && settings.lastRow === 'hide' && buildingRowResult === -1)) {
      this.clearBuildingRow();
      return;
    }

    if (this.maxRowHeight && this.maxRowHeight < this.buildingRow.height) {
      this.buildingRow.height = this.maxRowHeight;
    }

    // Align last (unjustified) row
    if (isLastRow && (settings.lastRow === 'center' || settings.lastRow === 'right')) {
      let availableWidth =
        this.galleryWidth - 2 * this.border - (this.buildingRow.entriesBuff.length - 1) * settings.margins;

      for (const entry of this.buildingRow.entriesBuff) {
        availableWidth -= this.entryJWidths.get(entry) ?? 0;
      }

      if (settings.lastRow === 'center') offX += Math.round(availableWidth / 2);
      else if (settings.lastRow === 'right') offX += availableWidth;
    }

    for (const entry of this.buildingRow.entriesBuff) {
      this.displayEntry(entry, offX, this.offY, this.buildingRow.height);
      offX += (this.entryJWidths.get(entry) ?? 0) + settings.margins;
    }

    // Gallery Height
    this.galleryHeightToSet = this.offY + this.buildingRow.height + this.border;
    this.setGalleryTempHeight(this.galleryHeightToSet);

    if (!isLastRow || (this.buildingRow.height <= settings.rowHeight && buildingRowResult)) {
      // Ready for a new row
      this.offY += this.buildingRow.height + settings.margins;
      this.rows += 1;
      this.clearBuildingRow();
      this.settings.triggerEvent.call(this, 'jg.rowflush');
    }
  }

  // Scroll position not restoring: https://github.com/miromannino/Justified-Gallery/issues/221
  galleryPrevStaticHeight = 0;

  private rememberGalleryHeight() {
    this.galleryPrevStaticHeight = this.galleryHeight;
    this.galleryHeight = this.galleryPrevStaticHeight;
  }

  // grow only
  private setGalleryTempHeight(height: number) {
    this.galleryPrevStaticHeight = Math.max(height, this.galleryPrevStaticHeight);
    this.galleryHeight = this.galleryPrevStaticHeight;
  }

  private setGalleryFinalHeight(height: number) {
    this.galleryPrevStaticHeight = height;
    this.galleryHeight = height;
  }

  /**
   * Checks the width of the gallery container, to know if a new justification is needed
   */
  private checkWidth() {
    this.checkWidthIntervalId = setInterval(() => {
      // if the gallery is not currently visible, abort.
      if (!(this.gallery.offsetWidth || this.gallery.offsetHeight || this.gallery.getClientRects().length > 0)) return;

      const galleryWidth = this.gallery.getBoundingClientRect().width;
      if (Math.abs(galleryWidth - this.galleryWidth) > this.settings.refreshSensitivity) {
        this.galleryWidth = galleryWidth;
        this.rewind();

        this.rememberGalleryHeight();

        // Restart to analyze
        this.startImgAnalyzer(true);
      }
    }, this.settings.refreshTime);
  }

  /**
   * Rewind the image analysis to start from the first entry.
   */
  rewind() {
    this.lastFetchedEntry = 0;
    this.lastAnalyzedIndex = -1;
    this.offY = this.border;
    this.rows = 0;
    this.clearBuildingRow();
  }

  /**
   * @returns {Array} all entries matched by `settings.selector`
   */
  private getAllEntries() {
    return [...this.gallery.querySelectorAll<HTMLDivElement>(this.settings.selector)];
  }

  /**
   * Update the entries searching it from the justified gallery HTML element
   *
   * @param norewind if norewind only the new entries will be changed (i.e. randomized)
   * @returns {boolean} true if some entries has been founded
   */
  updateEntries(norewind: boolean) {
    const allEntries = this.getAllEntries();
    let newEntries: HTMLElement[];

    if (norewind && this.lastFetchedEntry > 0) {
      newEntries = allEntries.slice(this.lastFetchedEntry);
    } else {
      this.entries = [];
      newEntries = allEntries;
    }

    if (newEntries.length > 0) {
      // If randomize
      if (this.settings.randomize) {
        newEntries = this.shuffleArray(newEntries);
      }

      this.entries = [...this.entries, ...newEntries];

      this.lastFetchedEntry = this.entries.length;

      return true;
    }

    return false;
  }

  /**
   * Apply the entries order to the DOM, iterating the entries and appending the images
   *
   * @param entries the entries that has been modified and that must be re-ordered in the DOM
   */
  private insertToGallery(entries: HTMLElement[]) {
    for (const entry of entries) {
      this.gallery.append(entry);
    }
  }

  /**
   * Shuffle the array using the Fisher-Yates shuffle algorithm
   *
   * @param array the array to shuffle
   * @return the shuffled array
   */
  private shuffleArray(array: HTMLElement[]): HTMLElement[] {
    for (let index = array.length - 1; index > 0; index--) {
      const random = Math.floor(Math.random() * (index + 1));
      const temporary = array[index];
      array[index] = array[random];
      array[random] = temporary;
    }
    this.insertToGallery(array);
    return array;
  }

  /**
   * Destroy the Justified Gallery instance.
   *
   * It clears all the css properties added in the style attributes. We doesn't backup the original
   * values for those css attributes, because it costs (performance) and because in general one
   * shouldn't use the style attribute for an uniform set of images (where we suppose the use of
   * classes). Creating a backup is also difficult because JG could be called multiple times and
   * with different style attributes.
   */
  destroy() {
    clearInterval(this.checkWidthIntervalId);
    this.stopImgAnalyzerStarter();

    // Get fresh entries list since filtered entries are absent in `this.entries`
    for (const entry of this.getAllEntries()) {
      // Reset entry style
      entry.style.width = '';
      entry.style.height = '';
      entry.style.top = '';
      entry.style.left = '';
      entry.classList.remove('jg-entry', 'jg-entry-visible');

      // Reset image style
      const image = this.imgFromEntry(entry);
      if (image) {
        image.style.width = '';
        image.style.height = '';
        image.style.marginLeft = '';
        image.style.marginTop = '';
      }
    }

    this.gallery.style.height = '';
    this.gallery.classList.remove('justified-gallery');
    this.settings.triggerEvent.call(this, 'jg.destroy');
  }

  /**
   * Analyze the images and builds the rows. It returns if it found an image that is not loaded.
   *
   * @param isForResize if the image analyzer is called for resizing or not, to call a different callback at the end
   */
  private analyzeImages(isForResize: boolean) {
    for (let index = this.lastAnalyzedIndex + 1; index < this.entries.length; index++) {
      const entry = this.entries[index];
      if (this.entryLoaded.get(entry) === 'true' || this.entryLoaded.get(entry) === 'skipped') {
        const availableWidth =
          this.galleryWidth - 2 * this.border - (this.buildingRow.entriesBuff.length - 1) * this.settings.margins;
        const imgAspectRatio = (this.entryWidths.get(entry) ?? 1) / (this.entryHeights.get(entry) ?? 1);

        this.buildingRow.entriesBuff.push(entry);
        this.buildingRow.aspectRatio += imgAspectRatio;
        this.buildingRow.width += imgAspectRatio * this.settings.rowHeight;
        this.lastAnalyzedIndex = index;

        if (availableWidth / (this.buildingRow.aspectRatio + imgAspectRatio) < this.settings.rowHeight) {
          this.flushRow(false, this.settings.maxRowsCount > 0 && this.rows === this.settings.maxRowsCount);

          if (++this.yield.flushed >= this.yield.every) {
            this.startImgAnalyzer(isForResize);
            return;
          }
        }
      } else if (this.entryLoaded.get(entry) !== 'error') {
        return;
      }
    }

    // Last row flush (the row is not full)
    if (this.buildingRow.entriesBuff.length > 0) {
      this.flushRow(true, this.settings.maxRowsCount > 0 && this.rows === this.settings.maxRowsCount);
    }

    /* Stop, if there is, the timeout to start the analyzeImages.
    This is because an image can be set loaded, and the timeout can be set,
    but this image can be analyzed yet.
    */
    this.stopImgAnalyzerStarter();

    this.setGalleryFinalHeight(this.galleryHeightToSet ?? 0);

    // On complete callback
    this.settings.triggerEvent.call(this, isForResize ? 'jg.resize' : 'jg.complete');
  }

  /**
   * Stops any ImgAnalyzer starter (that has an assigned timeout)
   */
  private stopImgAnalyzerStarter() {
    this.yield.flushed = 0;
    if (this.imgAnalyzerTimeout !== undefined) {
      clearTimeout(this.imgAnalyzerTimeout);
      this.imgAnalyzerTimeout = undefined;
    }
  }

  /**
   * Starts the image analyzer. It is not immediately called to let the browser to update the view
   *
   * @param isForResize specifies if the image analyzer must be called for resizing or not
   */
  private startImgAnalyzer(isForResize: boolean) {
    this.stopImgAnalyzerStarter();
    this.imgAnalyzerTimeout = setTimeout(() => {
      this.analyzeImages(isForResize);
    }, 0.001); // we can't start it immediately due to a IE different behavior
  }

  /**
   * Checks if the image is loaded or not using another image object. We cannot use the 'complete' image property,
   * because some browsers, with a 404 set complete = true.
   *
   * @param image the image to load
   * @param onLoad callback that is called when the image has been loaded
   * @param onError callback that is called in case of an error
   */
  private onImageEvent(image: HTMLImageElement, onLoad: (image: HTMLImageElement) => void, onError?: () => void) {
    if (!onLoad && !onError) return;

    if (image.complete) {
      onLoad(image);
      return;
    }

    if (onLoad) {
      image.addEventListener(
        'load',
        () => {
          onLoad(image);
        },
        { once: true },
      );
    }
    if (onError) {
      image.addEventListener(
        'error',
        () => {
          onError();
        },
        { once: true },
      );
    }
  }

  /**
   * Init of Justified Gallery controlled
   * It analyzes all the entries starting theirs loading and calling the image analyzer (that works with loaded images)
   */
  init() {
    let imagesToLoad = false;
    let skippedImages = false;

    for (const entry of this.entries) {
      const image = this.imgFromEntry(entry);

      entry.classList.add('jg-entry');

      if (this.entryLoaded.get(entry) !== 'true' && this.entryLoaded.get(entry) !== 'skipped') {
        // Link Rel global overwrite
        if (this.settings.rel) entry.setAttribute('rel', this.settings.rel);

        // Link Target global overwrite
        if (this.settings.target) entry.setAttribute('target', this.settings.target);

        if (image) {
          /* If we have the height and the width, we don't wait that the image is loaded,
          but we start directly with the justification */
          if (this.settings.waitThumbnailsLoad === false) {
            const width = Number.parseFloat(image.getAttribute('width') ?? '0');
            const height = Number.parseFloat(image.getAttribute('height') ?? '0');

            if (!Number.isNaN(width) && !Number.isNaN(height)) {
              this.entryWidths.set(entry, width);
              this.entryHeights.set(entry, height);
              this.entryLoaded.set(entry, 'skipped');
              skippedImages = true;
              this.startImgAnalyzer(false);
              continue; // continue
            }
          }

          this.entryLoaded.set(entry, 'false');
          imagesToLoad = true;

          this.onImageEvent(
            image,
            (loadImg) => {
              // image loaded
              this.entryWidths.set(entry, loadImg.width);
              this.entryHeights.set(entry, loadImg.height);
              this.entryLoaded.set(entry, 'true');
              this.startImgAnalyzer(false);
            },
            () => {
              // image load error
              this.entryLoaded.set(entry, 'error');
              this.startImgAnalyzer(false);
            },
          );
        } else {
          this.entryWidths.set(entry, entry.getBoundingClientRect().width ?? 1);
          this.entryHeights.set(entry, entry.getBoundingClientRect().height ?? 1);
          this.entryLoaded.set(entry, 'true');
        }
      }
    }

    if (!imagesToLoad && !skippedImages) this.startImgAnalyzer(false);
    this.checkWidth();
  }

  /**
   * check and convert the maxRowHeight setting
   * requires rowHeight to be already set
   * TODO: should be always called when only rowHeight is changed
   * @return number or null
   */
  private retrieveMaxRowHeight(): number | undefined {
    let newMaxRowHeight;
    const rowHeight = this.settings.rowHeight;

    if (typeof this.settings.maxRowHeight === 'string') {
      newMaxRowHeight = /^\d+%$/.test(this.settings.maxRowHeight)
        ? (rowHeight * Number.parseFloat(/^(\d+)%$/.exec(this.settings.maxRowHeight)![1])) / 100
        : Number.parseFloat(this.settings.maxRowHeight);
    } else if (typeof this.settings.maxRowHeight === 'number') {
      newMaxRowHeight = this.settings.maxRowHeight;
    } else if (this.settings.maxRowHeight === false || this.settings.maxRowHeight === undefined) {
      return undefined;
    } else {
      throw new Error('maxRowHeight must be a number or a percentage');
    }

    // check if the converted value is not a number
    if (Number.isNaN(newMaxRowHeight)) throw new Error('invalid number for maxRowHeight');

    // check values, maxRowHeight must be >= rowHeight
    if (newMaxRowHeight < rowHeight) newMaxRowHeight = rowHeight;

    return newMaxRowHeight;
  }
}
